import { Link, NavLink, useLocation } from "react-router-dom";
import { useState } from "react";
import { Menu, X, ChevronDown, ExternalLink } from "lucide-react";
import { IMG } from "../data/content";
import { trackAcademyClick } from "../lib/analytics";
import ThemeToggle from "./ThemeToggle";

const ACADEMY_URL = "https://seedkeepersacademy.com/";

const links = [
  { to: "/about", label: "About" },
  {
    to: "/services",
    label: "Services",
    children: [
      { to: "/services", label: "Services Overview" },
      { to: "/training", label: "Training" },
      { to: "/infrastructure", label: "Infrastructure" },
      { to: "/permits", label: "Permits" },
      { to: "/bush-botanics", label: "Bush Botanics" },
      { to: "/bush-maps", label: "Bush Maps" },
      { href: "https://www.rangerbase.com", label: "RangerBase", external: true },
      { to: "/stewardship", label: "Stewardship" },
      { to: "/community-led-seed-collection", label: "Community-Led Seed Collection" },
    ],
  },
  {
    to: "/seed-intelligence",
    label: "Intelligence",
    children: [
      { to: "/seed-intelligence", label: "Seed Intelligence" },
      { to: "/genetics", label: "Genetics" },
      { to: "/seed-intelligence#spirit", label: "SPIRIT Platform" },
    ],
  },
  { href: ACADEMY_URL, label: "Academy", external: true },
  {
    to: "/resources",
    label: "Resources",
    children: [
      { to: "/resources", label: "Resources" },
      { to: "/stories", label: "Stories" },
      { to: "/projects", label: "Projects" },
    ],
  },
  { to: "/contact", label: "Contact" },
];

const slug = (label) => label.toLowerCase().replace(/\s+/g, "-");

export default function Header() {
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  const isGroupActive = (l) =>
    l.children
      ? l.children.some((c) => loc.pathname === c.to)
      : loc.pathname === l.to;

  return (
    <header className="bg-cream/85 backdrop-blur-xl border-b border-rule sticky top-0 z-40" data-testid="site-header">
      <div className="w-full px-4 md:px-6 lg:px-10 flex items-center justify-between h-16 md:h-20 gap-3 md:gap-5 lg:gap-6">
        <Link
          to="/"
          data-testid="logo-link"
          className="flex items-center flex-shrink-0 group"
          aria-label="SeedKeepers — home"
        >
          <img
            src={IMG.logo}
            alt="SeedKeepers"
            className="h-10 md:h-12 w-auto transition-transform group-hover:scale-[1.02]"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-3 lg:gap-6 xl:gap-8">
          {links.map((l) =>
            l.external ? (
              <a
                key={l.href}
                href={l.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackAcademyClick("header_top", "primary_cta")}
                data-testid={`nav-${slug(l.label)}`}
                className="inline-flex items-center gap-1.5 text-[9px] lg:text-[10px] xl:text-[11px] tracking-[0.2em] lg:tracking-[0.24em] uppercase font-mono font-medium hover:text-eucalypt transition-colors whitespace-nowrap text-ink/80"
              >
                {l.label}
                <ExternalLink size={11} className="opacity-60" />
              </a>
            ) : l.children ? (
              <div key={l.to} className="relative group">
                <NavLink
                  to={l.to}
                  data-testid={`nav-${slug(l.label)}`}
                  className={`inline-flex items-center gap-1 text-[9px] lg:text-[10px] xl:text-[11px] tracking-[0.18em] lg:tracking-[0.2em] uppercase font-mono font-medium hover:text-eucalypt transition-colors whitespace-nowrap ${
                    isGroupActive(l) ? "text-eucalypt" : "text-ink/80"
                  }`}
                >
                  {l.label}
                  <ChevronDown size={11} className="transition-transform group-hover:rotate-180" />
                </NavLink>
                <div className="absolute left-0 top-full pt-3 invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-opacity z-50">
                  <div
                    className="bg-card border border-rule rounded-xl shadow-[0_12px_32px_-12px_rgba(26,23,21,0.15)] min-w-[220px] py-2 overflow-hidden"
                    data-testid={`dropdown-${slug(l.label)}`}
                  >
                    {l.children.map((c) =>
                      c.external ? (
                        <a
                          key={c.href}
                          href={c.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => trackAcademyClick("header_dropdown", "primary_cta")}
                          data-testid={`nav-${slug(l.label)}-${slug(c.label)}`}
                          className="flex items-center justify-between gap-2 px-5 py-2.5 text-[10px] xl:text-[11px] tracking-[0.2em] uppercase font-mono font-medium hover:bg-parchment hover:text-eucalypt transition-colors text-ink/80"
                        >
                          <span>{c.label}</span>
                          <ExternalLink size={11} className="opacity-60" />
                        </a>
                      ) : (
                        <NavLink
                          key={`${c.to}-${c.label}`}
                          to={c.to}
                          data-testid={`nav-${slug(l.label)}-${slug(c.label)}`}
                          className={({ isActive }) =>
                            `block px-5 py-2.5 text-[10px] xl:text-[11px] tracking-[0.2em] uppercase font-mono font-medium hover:bg-parchment hover:text-eucalypt transition-colors ${
                              isActive ? "text-eucalypt" : "text-ink/80"
                            }`
                          }
                        >
                          {c.label}
                        </NavLink>
                      )
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${slug(l.label)}`}
                className={({ isActive }) =>
                  `text-[9px] lg:text-[10px] xl:text-[11px] tracking-[0.18em] lg:tracking-[0.2em] uppercase font-mono font-medium hover:text-eucalypt transition-colors whitespace-nowrap ${
                    isActive ? "text-eucalypt" : "text-ink/80"
                  }`
                }
              >
                {l.label}
              </NavLink>
            )
          )}
        </nav>

        <button
          className="md:hidden p-2 border border-rule rounded-full"
          onClick={() => setOpen((o) => !o)}
          data-testid="mobile-menu-toggle"
          aria-label="Menu"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
        <div className="flex items-center gap-2 flex-shrink-0">
          <ThemeToggle />
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-rule bg-cream" data-testid="mobile-menu">
          <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-3">
            {links.map((l) =>
              l.external ? (
                <a
                  key={l.href}
                  href={l.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => {
                    trackAcademyClick("mobile_menu", "primary_cta");
                    setOpen(false);
                  }}
                  data-testid={`mobile-nav-${slug(l.label)}`}
                  className="flex items-center gap-2 text-xs tracking-[0.22em] uppercase font-mono py-2 text-ink"
                >
                  <span>{l.label}</span>
                  <ExternalLink size={11} className="opacity-60" />
                </a>
              ) : l.children ? (
                <div key={l.to}>
                  <p className="text-[10px] tracking-[0.25em] uppercase font-mono text-muted py-2">
                    {l.label}
                  </p>
                  <div className="flex flex-col border-l border-rule ml-1 pl-4">
                    {l.children.map((c) =>
                      c.external ? (
                        <a
                          key={c.href}
                          href={c.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => {
                            trackAcademyClick("mobile_menu", "primary_cta");
                            setOpen(false);
                          }}
                          data-testid={`mobile-nav-${slug(l.label)}-${slug(c.label)}`}
                          className="flex items-center gap-2 text-xs tracking-[0.22em] uppercase font-mono py-2 text-ink"
                        >
                          <span>{c.label}</span>
                          <ExternalLink size={11} className="opacity-60" />
                        </a>
                      ) : (
                        <Link
                          key={`${c.to}-${c.label}`}
                          to={c.to}
                          onClick={() => setOpen(false)}
                          data-testid={`mobile-nav-${slug(l.label)}-${slug(c.label)}`}
                          className={`text-xs tracking-[0.22em] uppercase font-mono py-2 ${
                            loc.pathname === c.to ? "text-eucalypt" : "text-ink"
                          }`}
                        >
                          {c.label}
                        </Link>
                      )
                    )}
                  </div>
                </div>
              ) : (
                <Link
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${slug(l.label)}`}
                  className={`text-xs tracking-[0.22em] uppercase font-mono py-2 ${
                    loc.pathname === l.to ? "text-eucalypt" : "text-ink"
                  }`}
                >
                  {l.label}
                </Link>
              )
            )}
          </div>
        </div>
      )}
    </header>
  );
}
